// hooks/projects/useCurrentProject.js
import { useParams, useLocation } from 'react-router-dom';
import { useTranscript } from 'hooks/transcripts/queries';
import { useSurveyQuestions, useSurveyResponse, useSurveyQuestionResponses } from 'hooks/surveys/queries';

// Global fallback for when component unmounts
let globalLastKnownProjectId = null;

export const useCurrentProject = () => {
  const params = useParams();
  const { pathname } = useLocation();
  
  // Always call all hooks - pass null/undefined to disable queries when not needed
  const { data: transcript, isLoading: transcriptLoading } = useTranscript(
    params.transcriptID && pathname.includes('/transcripts/') ? params.transcriptID : null
  );
  
  const { data: surveyQuestionsData, isLoading: loadingQuestions } = useSurveyQuestions(
    pathname.includes('/surveys/') ? params.surveyID : null
  );
  
  const { data: surveyQuestionResponsesData, isLoading: loadingQuestionResponses } = useSurveyQuestionResponses(
    pathname.includes('/survey_questions/') ? params.surveyQuestionID : null
  );
  
  const { data: surveyResponseData, isLoading: loadingResponse } = useSurveyResponse(
    pathname.includes('/survey_responses/') ? params.surveyResponseID : null
  );

  // Determine current project ID and loading state based on route
  let currentProjectId = null;
  let isLoading = false;
  
  if (params.projectID) {
    // Direct project routes: /project/:projectId
    currentProjectId = parseInt(params.projectID);
  } else if (params.transcriptID && pathname.includes('/transcripts/')) {
    // Transcript routes
    currentProjectId = transcript?.project_id;
    isLoading = transcriptLoading;
  } else if (pathname.includes('/surveys/')) {
    // Survey routes
    currentProjectId = surveyQuestionsData?.project_id;
    isLoading = loadingQuestions;
  } else if (pathname.includes('/survey_questions/')) {
    // Survey question routes
    currentProjectId = surveyQuestionResponsesData?.project_id;
    isLoading = loadingQuestionResponses;
  } else if (pathname.includes('/survey_responses/')) {
    // Survey response routes
    currentProjectId = surveyResponseData?.project_id;
    isLoading = loadingResponse;
  }

  // Clear global project ID on /users page (account page)
  if (pathname.startsWith('/users')) {
    globalLastKnownProjectId = null;
  }
  // Update global fallback if we have a valid project ID
  else if (currentProjectId) {
    globalLastKnownProjectId = currentProjectId;
  }

  return {
    projectId: currentProjectId || globalLastKnownProjectId,
    isLoading: isLoading && !globalLastKnownProjectId,
  };
};